<template>
  <DefaultLayout body-class="pacsort-login">
    <div class="login-page">
      <a href="#login" class="skip-link">Skip to login</a>
      <main class="login-surface">
        <header class="brand-header">
          <a class="brand-mark" href="https://21grams.se/" aria-label="21grams home">
            <img src="@/assets/images/login-brand/mark.svg" width="110" height="110" alt="" />
          </a>
          <a class="brand-wordmark" href="https://pacsortonline.com/" aria-label="PacSort Online home">
            <img src="@/assets/images/login-brand/wordmark.svg" width="110" height="28" alt="21grams" />
          </a>
        </header>
        <section id="login" class="login-content" aria-labelledby="login-heading">
          <div class="intro">
            <h1 id="login-heading" class="product-name">PacSort Analytics</h1>
          </div>
          <form :aria-busy="submitting" @submit.prevent="logIn">
            <div v-if="error" class="login-error" role="alert">
              Unable to log in. Check your email and password and try again.
            </div>
            <div class="field">
              <label class="field-label-hidden" for="emailaddress">Email address</label>
              <input
                id="emailaddress"
                v-model="email"
                type="email"
                class="form-control"
                autocomplete="username"
                placeholder="Enter your email address"
                required
                :spellcheck="false"
                autocapitalize="none"
              />
            </div>
            <div class="field">
              <label class="field-label-hidden" for="password">Password</label>
              <div class="password-field">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  autocomplete="current-password"
                  placeholder="Enter your password"
                  required
                />
                <button
                  class="password-toggle"
                  type="button"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  aria-controls="password"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'mdi mdi-eye-off-outline' : 'mdi mdi-eye-outline'" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <button class="login-button" type="submit" :disabled="submitting">
              {{ submitting ? 'Logging in…' : 'Log in' }} <span aria-hidden="true">→</span>
            </button>
            <div class="form-options">
              <label class="remember" for="checkbox-signin">
                <input
                  id="checkbox-signin"
                  v-model="checked"
                  type="checkbox"
                  class="form-check-input remember-checkbox"
                />Keep me logged in
              </label>
              <router-link to="/recoverpw">Forgot password?</router-link>
            </div>
          </form>
          <a class="back-link" href="https://pacsortonline.com/">Go to PacSort Online <span aria-hidden="true">→</span></a>
        </section>
      </main>
      <footer class="login-footer">
        <div class="footer-main">
          <div class="footer-identity">
            <a href="https://21grams.se/" class="footer-logo" aria-label="21grams home">
              <img src="@/assets/images/login-brand/footer-mark.svg" width="32" height="48" alt="" />
              <img src="@/assets/images/login-brand/footer-wordmark.svg" width="110" height="48" alt="21grams" />
            </a>
            <p>PacSort Analytics</p>
          </div>
          <div class="support">
            <a class="contact-link" href="https://21grams.se/kontakt/">Contact us <span aria-hidden="true">→</span></a>
            <a href="mailto:support@21grams.com">support@21grams.com</a>
            <a href="tel:004686003721">+46 (8) 600 37 21 · option 2</a>
          </div>
        </div>
        <div class="footer-bottom">
          <span>© {{ new Date().getFullYear() }} 21grams</span>
          <nav aria-label="Footer">
            <a href="https://pacsortonline.com/info/privacy-policy/">Privacy Policy</a>
            <a href="https://pacsortonline.com/info/additional-charges/">Terms of Use</a>
            <a href="https://pacsortonline.com/faq">FAQ</a>
          </nav>
        </div>
      </footer>
    </div>
  </DefaultLayout>
</template>

<script lang="ts">
import { defineComponent, ref, type Ref } from 'vue'
import { useAuthStore } from '@/requests/Auth'
import { useRoute } from 'vue-router'
import DefaultLayout from '@/views/components/layouts/default-layout.vue'

export default defineComponent({
  components: { DefaultLayout },
  setup() {
    const checked: Ref<boolean> = ref(true)
    const email: Ref<string> = ref('')
    const password: Ref<string> = ref('')
    const error: Ref<boolean> = ref(false)
    const showPassword = ref(false)
    const submitting = ref(false)

    const auth = useAuthStore()
    const route = useRoute()

    const logIn = () => {
      if (submitting.value) return
      error.value = false
      submitting.value = true
      auth
        .logIn(email.value, password.value)
        .then((success) => {
          if (success) {
            const redirectFrom = route.query?.redirectFrom?.toString()
            window.location.href = redirectFrom || '/'
          } else {
            error.value = true
          }
        })
        .catch(() => {
          error.value = true
        })
        .finally(() => {
          submitting.value = false
        })
    }

    return {
      showPassword,
      submitting,
      checked,
      email,
      password,
      error,
      auth,
      logIn,
    }
  },
})
</script>

<style scoped src="./auth-brand.css"></style>
